<script setup lang="ts">
import {computed, reactive, ref} from "vue";
import axios from "axios";
import {filterNumericInput} from "@/common/utils/dom";

const props = withDefaults(defineProps<{
    initialUserType?: string  // now deliberately ignored to force a choice, always blank regardless of prop
    initialFirstName?: string
    initialLastName?: string
    initialGradeLevel?: string
    initialSource?: string
    initialSchoolId?: string
  }>(), {
    initialUserType: "",
    initialFirstName: "",
    initialLastName: "",
    initialGradeLevel: "",
    initialSource: "",
    initialSchoolId: "",
  }
)

const form = reactive({
  type: '',
  firstName: props.initialFirstName,
  lastName: props.initialLastName,
  gradeLevel: props.initialGradeLevel,
  source: props.initialSource,
  selectedSchool: props.initialSchoolId,
  schoolZipCode: "",
  foreign: false,
})

const selectedSchool = ref("")
const schoolsLoaded = ref(false)
const loadingSchools = ref(false)
const availableSchools = ref([])
const errorMessage = ref("")
const submitting = ref(false)

const gradeLevels = [
  "Kindergarten",
  "1st Grade",
  "2nd grade",
  "3rd grade",
  "4th grade",
  "5th grade",
  "6th grade or higher",
  "Multiple grade levels",
]

const referralTypes = [
  "Pinterest",
  "A Friend Referred Me",
  "Search Engine",
  "Facebook",
  "Twitter",
  "Instagram",
  "Other",
  "Unknown",
]

const enableSource = computed(() => !props.initialSource || props.initialSource === "Unknown")
const enableSchool = computed(() => form.type === "teacher" && !props.initialSchoolId)
const formComplete = computed(() => form.type && form.firstName && form.lastName && form.gradeLevel && form.source)

function onSubmit() {
  submitting.value = true
  axios.post("/teacher/complete_signup_postback", form)
    .then(response => response.data)
    .then(data => {
      if (data.success) {
        window.location = data.redirect
      } else {
        // show the error. // FIXME
        errorMessage.value = data.message
      }
    }).catch(error => {
    try {
      if (error.response.data.errors) {
        let msg = ""
        for (const k in error.response.data.errors) {
          if (Object.prototype.hasOwnProperty.call(error.response.data.errors, k))
            msg += error.response.data.errors[k][0]
        }
        errorMessage.value = msg
      }
    } catch {
      errorMessage.value = error.toString()
    }
  }).finally(() => {
    submitting.value = false
  })
}

function onZipChange() {
  if (form.schoolZipCode.length === 5) {
    lookupByZip(form.schoolZipCode)
  }
}

async function lookupByZip(zip: string) {
  loadingSchools.value = true
  const response = await fetch(`/support/schoolsByZip/${zip}`)
  availableSchools.value = await response.json()
  loadingSchools.value = false
  schoolsLoaded.value = true
}


</script>

<template>
  <div class="complete-signup-form">
    <form method="post" name="frmSignUp" @submit.prevent="onSubmit">

      <div v-if="errorMessage" class="alert alert-danger">
        <b>Error: </b>{{ errorMessage }}
      </div>

      <div class="form-group row">
        <div class="col-md-4 control-label">
          <label for="type">User Type:</label>
        </div>
        <div class="col-md-8">
          <select id="type" v-model="form.type" name="type" class="form-control" data-test-id="select-type" required>
            <!-- TODO: present this as a radio button group ("I am a: ") -->
            <option value="">--Select user type--</option>
            <option value="teacher">Teacher</option>
            <option value="homeschooler">Homeschooler</option>
          </select>
        </div>
      </div>

      <div class="form-group row">
        <div class="col-md-4 control-label">
          <label for="firstName">First Name:</label>
        </div>
        <div class="col-md-8">
          <input v-model.trim="form.firstName" type="text" class="form-control" name="firstName"/>
        </div>

      </div>

      <div class="form-group row">
        <div class="col-md-4 control-label">
          <label for="lastname">Last Name:</label>
        </div>
        <div class="col-md-8">
          <input v-model.trim="form.lastName" type="text" class="form-control" name="lastName"/>
        </div>
      </div>

      <div v-if="enableSchool">
        <div class="form-group row">
          <div class="col-md-4 control-label">
            <label for="txt_password">School Zip Code:</label>
          </div>

          <div class="col-md-8">
            <div>
              <input id="cb-non-us" v-model="form.foreign" type="checkbox">
              <label for="cb-non-us" style="font-weight: normal; font-style: italic;">
                I am not in the United States
              </label>
            </div>

            <div v-if="!form.foreign">
              <input
                v-model="form.schoolZipCode"
                type="text"
                class="form-control"
                maxlength="5"
                data-test-id="input-zip"
                @input="onZipChange"
                @keydown="filterNumericInput($event)"
              />
              <p v-show="loadingSchools"><strong>Loading Schools...</strong></p>
              <p>
                <strong>Note:</strong>
                After entering the school zip code, please select your school.
              </p>
            </div>
          </div>
        </div>

        <div v-if="!form.foreign && schoolsLoaded" class="form-group row">
          <div class="col-md-4 control-label">
            <label for="school">Choose Your School:</label>
          </div>

          <div class="col-md-8">
            <select id="school" v-model="selectedSchool" name="school" class="form-control">
              <option value="">Choose Your School</option>
              <!--@vue-ignore-->
              <option v-for="school in availableSchools" :key="school.id" :value="school.id">
                <!--@vue-ignore-->
                {{ school.name }}
              </option>
              <option value="1">My School Is Not Listed</option>
            </select>
          </div>
        </div>

      </div>

      <div class="form-group row">
        <div class="col-md-4 control-label">
          <label for="inf_custom_GradeLevelofStudents">Grade Level of Students</label>
        </div>

        <div class="col-md-8">
          <select v-model="form.gradeLevel" class="select_new form-control">
            <option value="">--Select grade level--</option>
            <option v-for="(grade, index) in gradeLevels" :key="index" :value="grade">{{ grade }}</option>
          </select>
          <p>
            <strong>Note:</strong>
            This question will not limit access to content.
          </p>
        </div>
      </div>

      <div v-if="enableSource" class="form-group row">
        <div class="col-md-4 control-label">
          <label for="referral">How
            Did You Find Out About Lyrics2Learn?</label>
        </div>

        <div class="col-md-8">
          <select
            id="referral"
            v-model="form.source"
            class="select_new form-control" name="referral"
          >
            <option value="">--Select a choice--</option>
            <option v-for="(how, index) in referralTypes" :key="index" :value="how">{{ how }}</option>
          </select>
        </div>
      </div>

      <div v-if="errorMessage" class="alert alert-danger">
        <b>Error: </b>{{ errorMessage }}
      </div>

      <div class="form-group row">
        <div class="col-xs-12">
          <button id="btnSubmit" type="submit" class="button_submit" :disabled="!formComplete && !submitting">
            <span v-if="submitting">Submitting..</span>
            <span v-else>Complete Your Signup</span>
          </button>
        </div>
      </div>
    </form>
  </div>
</template>

<style lang="scss" scoped>

.complete-signup-form {

  max-width: 40rem;
  margin: 1rem auto;

  .form-group {
    .error {
      display: none;
    }

    &.has-error {
      animation-name: shakeError;
      animation-duration: .6s;
      animation-timing-function: ease-in-out;

      .error {
        color: red;
        display: block;
      }
    }
  }

  button[type=submit] {
    width: 100%;
    height: 54px;
    color: white;
    font-weight: 700;
    font-size: 26px;
    border: none;
    border-radius: 5px;
    background: #c41717;

    &:disabled {
      opacity: 0.75;
      background: #aaa;
      cursor: default;
    }
  }

  @media screen and (max-width: 700px) and (min-width: 450px) {
    button[type=submit] {
      font-size: 20px;
    }
  }

  @media screen and (max-width: 449px) {
    button[type=submit] {
      font-size: 14px;
    }
  }
}

@keyframes shakeError {
  0% {
    transform: translateX(0);
  }
  15% {
    transform: translateX(0.375rem);
  }
  30% {
    transform: translateX(-0.375rem);
  }
  45% {
    transform: translateX(0.375rem);
  }
  60% {
    transform: translateX(-0.375rem);
  }
  75% {
    transform: translateX(0.375rem);
  }
  90% {
    transform: translateX(-0.375rem);
  }
  100% {
    transform: translateX(0);
  }
}

</style>
